// Étape 4. Composant Seo centralisé
// Injection dynamique de <title>, meta description, canonical, hreflang, OG, Twitter et Schema.org
// selon la route active et la langue courante, sans dépendance externe.
import { useEffect } from "react";
import { useLocation, useMatch } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import {
 SITE_URL,
 SITE_NAME,
 DEFAULT_OG_IMAGE,
 LOCALE_MAP,
 SEO_ROUTES,
 BRAND_SEO,
 localeIndex,
} from "@/i18n/seoConfig";
import { BRANDS } from "@/data/brands";
import { TRESORS } from "@/data/tresors";

const upsertMeta = (attr, key, value) => {
 if (!value) return;
 let el = document.head.querySelector(`meta[${attr}="${key}"]`);
 if (!el) {
 el = document.createElement("meta");
 el.setAttribute(attr, key);
 document.head.appendChild(el);
 }
 el.setAttribute("content", value);
};

const upsertLink = (rel, href, extra = {}) => {
 const selector = extra.hreflang
 ? `link[rel="${rel}"][hreflang="${extra.hreflang}"]`
 : `link[rel="${rel}"]`;
 let el = document.head.querySelector(selector);
 if (!el) {
 el = document.createElement("link");
 el.setAttribute("rel", rel);
 if (extra.hreflang) el.setAttribute("hreflang", extra.hreflang);
 document.head.appendChild(el);
 }
 el.setAttribute("href", href);
};

// Nettoie les scripts Schema.org précédemment injectés par ce composant
const clearDynamicJsonLd = () => {
 document.head.querySelectorAll('script[data-seo="dynamic"]').forEach((s) => s.remove);
};

const injectJsonLd = (data) => {
 const s = document.createElement("script");
 s.setAttribute("type", "application/ld+json");
 s.setAttribute("data-seo", "dynamic");
 s.textContent = JSON.stringify(data);
 document.head.appendChild(s);
};

// Construit le fil d'Ariane pour Schema.org
const buildBreadcrumb = (pathname, labelByLocale, locale) => {
 const segments = pathname.split("/").filter(Boolean);
 const items = [
 { name: locale === "en" ? "Home" : locale === "pt" ? "Início" : "Accueil", url: SITE_URL + "/" },
 ];
 let acc = "";
 segments.forEach((seg) => {
 acc += "/" + seg;
 items.push({ name: labelByLocale[seg] || seg, url: SITE_URL + acc });
 });
 return {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: items.map((it, i) => ({
 "@type": "ListItem",
 position: i + 1,
 name: it.name,
 item: it.url,
 })),
 };
};

const SEG_LABELS = {
 fr: { maison: "La Maison", marques: "Marques", secteurs: "Secteurs", tresors: "Trésors", carnet: "Carnet", actualites: "Actualités", impact: "Impact", partenaires: "Partenaires", rejoindre: "Nous rejoindre", contact: "Contact", identite: "Identité", legal: "Mentions légales" },
 en: { maison: "The House", marques: "Brands", secteurs: "Sectors", tresors: "Treasures", carnet: "Journal", actualites: "News", impact: "Impact", partenaires: "Partners", rejoindre: "Join us", contact: "Contact", identite: "Identity", legal: "Legal" },
 pt: { maison: "A Casa", marques: "Marcas", secteurs: "Sectores", tresors: "Tesouros", carnet: "Caderno", actualites: "Actualidades", impact: "Impacto", partenaires: "Parceiros", rejoindre: "Junte-se", contact: "Contacto", identite: "Identidade", legal: "Legal" },
};

export default function Seo() {
 const { pathname } = useLocation();
 const { locale } = useLanguage();
 const idx = localeIndex(locale);
 const brandMatch = useMatch("/marques/:slug");
 const tresorMatch = useMatch("/tresors/:slug");
 const articleMatch = useMatch("/carnet/:slug");

 useEffect(() => {
 // 1. Détermine la config SEO active
 let title;
 let description;
 let schemaKind = "generic";
 let ogImage = DEFAULT_OG_IMAGE;
 let ogType = "website";
 let extraSchema = null;

 if (brandMatch) {
 const slug = brandMatch.params.slug;
 const brand = BRANDS.find((b) => b.slug === slug);
 const seo = BRAND_SEO[slug];
 if (brand && seo) {
 title = seo.title[idx];
 description = seo.description[idx];
 ogImage = brand.image || DEFAULT_OG_IMAGE;
 schemaKind = "brand";
 if (slug === "energia-pro") {
 // Schema.org spécifique ENERGIA PRO : Organization + OfferCatalog (13 domaines) + FAQPage inline
 const DOMAINS_EP = [
 { fr: "Réfrigération industrielle et commerciale", en: "Industrial and commercial refrigeration", pt: "Refrigeração industrial e comercial" },
 { fr: "Chambres froides positives et négatives", en: "Positive and negative cold rooms", pt: "Câmaras frigoríficas positivas e negativas" },
 { fr: "Climatisation professionnelle", en: "Professional air conditioning", pt: "Climatização profissional" },
 { fr: "Groupes frigorifiques, évaporateurs, condenseurs", en: "Refrigeration units, evaporators, condensers", pt: "Grupos frigoríficos, evaporadores, condensadores" },
 { fr: "Panneaux isothermes, portes et accessoires", en: "Isothermal panels, doors and accessories", pt: "Painéis isotérmicos, portas e acessórios" },
 { fr: "Énergie solaire photovoltaïque", en: "Solar photovoltaic energy", pt: "Energia solar fotovoltaica" },
 { fr: "Stockage lithium LiFePO₄, onduleurs, hybride", en: "LiFePO₄ lithium storage, inverters, hybrid", pt: "Armazenamento lítio LiFePO₄, inversores, híbrido" },
 { fr: "Outillage frigoriste et instruments de mesure", en: "Refrigeration tools and measuring instruments", pt: "Ferramentas frigoristas e instrumentos de medição" },
 { fr: "Connectique, câblage, protections électriques", en: "Connectors, wiring, electrical protections", pt: "Ligações, cablagem, protecções eléctricas" },
 { fr: "Consommables professionnels (fluides, huiles, brasures)", en: "Professional consumables (refrigerants, oils, brazing)", pt: "Consumíveis profissionais (fluidos, óleos, soldaduras)" },
 { fr: "Quincaillerie professionnelle", en: "Professional hardware", pt: "Ferragens profissionais" },
 { fr: "Matériel et consommables de soudure", en: "Welding equipment and consumables", pt: "Material e consumíveis de soldadura" },
 { fr: "Équipements de protection individuelle (EPI)", en: "Personal protective equipment (PPE)", pt: "Equipamentos de protecção individual (EPI)" },
 ];
 const langKey = locale === "en" ? "en" : locale === "pt" ? "pt" : "fr";
 extraSchema = {
 "@context": "https://schema.org",
 "@type": "Organization",
 "@id": `${SITE_URL}/marques/energia-pro#organization`,
 name: "NANDA SÁ ENERGIA PRO",
 alternateName: ["NANDA ENERGIA PRO", "Nanda Energia Pro"],
 description: description,
 url: `${SITE_URL}/marques/energia-pro`,
 logo: `${SITE_URL}/logo.svg`,
 image: brand.image,
 inLanguage: ["fr", "pt", "en"],
 parentOrganization: {
 "@type": "Organization",
 name: "NANDA SÁ",
 url: SITE_URL,
 },
 contactPoint: [
 {
 "@type": "ContactPoint",
 contactType: "sales",
 email: "hello@nanda-sa.com",
 telephone: "+245956828351",
 areaServed: "GW",
 availableLanguage: ["fr", "pt", "en"],
 },
 {
 "@type": "ContactPoint",
 contactType: "sales",
 email: "hello@nanda-sa.com",
 telephone: "+221710090410",
 areaServed: "SN",
 availableLanguage: ["fr", "en"],
 },
 ],
 areaServed: [
 { "@type": "Country", name: "Senegal" },
 { "@type": "Country", name: "Guinea-Bissau" },
 { "@type": "City", name: "Ziguinchor", containedInPlace: { "@type": "Country", name: "Senegal" } },
 { "@type": "City", name: "Canchungo", containedInPlace: { "@type": "Country", name: "Guinea-Bissau" } },
 { "@type": "AdministrativeArea", name: "Casamance" },
 { "@type": "AdministrativeArea", name: "Cacheu" },
 "West Africa"
 ],
 knowsAbout: [
 "Industrial refrigeration", "Cold chain", "Cold rooms",
 "Professional air conditioning", "HVAC",
 "Solar photovoltaic energy", "Off-grid solar", "Solar pumping",
 "LiFePO₄ lithium batteries", "Hybrid inverters",
 "Refrigeration units", "Evaporators", "Condensers",
 "Isothermal panels", "Refrigeration tools",
 "Refrigerants", "PPE", "Professional consumables",
 "West Africa", "Senegal", "Guinea-Bissau", "Ziguinchor", "Casamance", "Canchungo", "Cacheu",
 "Solar installation", "Photovoltaic panels", "Professional hardware", "Welding equipment and consumables",
 ],
 hasOfferCatalog: {
 "@type": "OfferCatalog",
 name: langKey === "en" ? "NANDA SÁ ENERGIA PRO fields of expertise" : langKey === "pt" ? "Domínios de especialização NANDA SÁ ENERGIA PRO" : "Domaines d'expertise NANDA SÁ ENERGIA PRO",
 itemListElement: DOMAINS_EP.map((d, i) => ({
 "@type": "Offer",
 position: i + 1,
 itemOffered: {
 "@type": "Service",
 name: d[langKey],
 provider: { "@type": "Organization", "@id": `${SITE_URL}/marques/energia-pro#organization` },
 areaServed: ["Senegal", "Guinea-Bissau", "West Africa"],
 },
 })),
 },
 sameAs: [
 "https://www.instagram.com/nandasa_africa",
 "https://www.linkedin.com/company/nanda-s%C3%A1/",
 "https://www.facebook.com/nandasa.africa",
 ],
 };
 } else if (slug === "academie") {
 extraSchema = {
 "@context": "https://schema.org",
 "@type": "EducationalOrganization",
 "@id": `${SITE_URL}/marques/academie#organization`,
 name: "NANDA SÁ Académie",
 alternateName: ["NANDA SA Académie", "Académie NANDA SÁ", "NANDA SÁ Academy"],
 description: description,
 url: `${SITE_URL}/marques/academie`,
 logo: `${SITE_URL}/logo.svg`,
 image: brand.image,
 inLanguage: ["fr", "pt", "en"],
 slogan: "Former aujourd'hui les talents qui construiront l'Afrique de demain.",
 parentOrganization: {
 "@type": "Organization",
 name: "NANDA SÁ",
 url: SITE_URL,
 },
 contactPoint: [
 {
 "@type": "ContactPoint",
 contactType: "customer service",
 email: "hello@nanda-sa.com",
 telephone: "+245956828351",
 areaServed: "GW",
 availableLanguage: ["fr", "pt", "en"],
 },
 {
 "@type": "ContactPoint",
 contactType: "customer service",
 email: "hello@nanda-sa.com",
 telephone: "+221710090410",
 areaServed: "SN",
 availableLanguage: ["fr", "en"],
 },
 ],
 areaServed: [
 { "@type": "Country", name: "Senegal" },
 { "@type": "Country", name: "Guinea-Bissau" },
 { "@type": "City", name: "Ziguinchor", containedInPlace: { "@type": "Country", name: "Senegal" } },
 { "@type": "City", name: "Canchungo", containedInPlace: { "@type": "Country", name: "Guinea-Bissau" } },
 ],
 hasOfferCatalog: {
 "@type": "OfferCatalog",
 name: "Pôles de formation NANDA SÁ Académie",
 itemListElement: [
 {
 "@type": "Course",
 name: "Éducation",
 description: "Soutien scolaire, alphabétisation, langues et développement des compétences essentielles pour jeunes et adultes.",
 provider: { "@type": "EducationalOrganization", name: "NANDA SÁ Académie", "@id": `${SITE_URL}/marques/academie#organization` },
 inLanguage: ["fr", "pt", "en"],
 },
 {
 "@type": "Course",
 name: "Numérique",
 description: "Outils numériques, bureautique, intelligence artificielle, codage et compétences digitales.",
 provider: { "@type": "EducationalOrganization", name: "NANDA SÁ Académie", "@id": `${SITE_URL}/marques/academie#organization` },
 inLanguage: ["fr", "pt", "en"],
 },
 {
 "@type": "Course",
 name: "Énergie",
 description: "Énergies renouvelables, installation solaire photovoltaïque, maintenance et efficacité énergétique.",
 provider: { "@type": "EducationalOrganization", name: "NANDA SÁ Académie", "@id": `${SITE_URL}/marques/academie#organization` },
 inLanguage: ["fr", "pt", "en"],
 },
 {
 "@type": "Course",
 name: "Sport",
 description: "Discipline, confiance en soi, leadership, travail d'équipe et bien-être par la pratique sportive.",
 provider: { "@type": "EducationalOrganization", name: "NANDA SÁ Académie", "@id": `${SITE_URL}/marques/academie#organization` },
 inLanguage: ["fr", "pt", "en"],
 },
 {
 "@type": "Course",
 name: "Entrepreneuriat",
 description: "Gestion, commerce, marketing, finance et développement de projet pour créateurs d'entreprise.",
 provider: { "@type": "EducationalOrganization", name: "NANDA SÁ Académie", "@id": `${SITE_URL}/marques/academie#organization` },
 inLanguage: ["fr", "pt", "en"],
 },
 ],
 },
 sameAs: [
 "https://www.instagram.com/nandasa_africa",
 "https://www.linkedin.com/company/nanda-s%C3%A1/",
 "https://www.facebook.com/nandasa.africa",
 ],
 };
 } else {
 extraSchema = {
 "@context": "https://schema.org",
 "@type": "Brand",
 name: brand.name,
 description: description,
 url: `${SITE_URL}/marques/${slug}`,
 logo: brand.image,
 parentOrganization: {
 "@type": "Organization",
 name: "NANDA SÁ",
 url: SITE_URL,
 },
 };
 }
 }
 } else if (tresorMatch) {
 const slug = tresorMatch.params.slug;
 const tresor = TRESORS.find((t) => t.slug === slug);
 if (tresor) {
 const teaserFR = typeof tresor.teaser === "string" ? tresor.teaser : (tresor.teaser?.fr || tresor.teaser);
 title = `${tresor.name}. Trésor d'Afrique de l'Ouest | NANDA SÁ`;
 if (locale === "en") title = `${tresor.name}. West African Treasure | NANDA SÁ`;
 if (locale === "pt") title = `${tresor.name}. Tesouro da África Ocidental | NANDA SÁ`;
 description = typeof teaserFR === "string" ? teaserFR : String(teaserFR);
 ogImage = tresor.image;
 schemaKind = "tresor";
 extraSchema = {
 "@context": "https://schema.org",
 "@type": "Thing",
 name: tresor.name,
 alternateName: tresor.latin,
 description: description,
 image: tresor.image,
 url: `${SITE_URL}/tresors/${slug}`,
 };
 }
 } else if (articleMatch) {
 title = locale === "en"
 ? "Article. NANDA SÁ Journal"
 : locale === "pt"
 ? "Artigo. Caderno NANDA SÁ"
 : "Article. Le Carnet NANDA SÁ";
 description = locale === "en"
 ? "An editorial story from NANDA SÁ, encounters, know-how and West African heritage."
 : locale === "pt"
 ? "Um relato editorial do NANDA SÁ, encontros, saberes e património oeste-africano."
 : "Un récit éditorial de NANDA SÁ, rencontres, savoir-faire et patrimoine ouest-africain.";
 ogType = "article";
 schemaKind = "article";
 } else if (pathname.startsWith("/legal/")) {
 const cfg = SEO_ROUTES[pathname];
 if (cfg) {
 title = cfg.title[idx];
 description = cfg.description[idx];
 schemaKind = cfg.schema;
 } else {
 title = locale === "en" ? "Legal information. NANDA SÁ" : locale === "pt" ? "Informações legais. NANDA SÁ" : "Informations légales. NANDA SÁ";
 description = locale === "en" ? "Legal notices, privacy policy and cookies." : locale === "pt" ? "Menções legais, política de privacidade e cookies." : "Mentions légales, politique de confidentialité et cookies.";
 }
 } else {
 const cfg = SEO_ROUTES[pathname];
 if (cfg) {
 title = cfg.title[idx];
 description = cfg.description[idx];
 schemaKind = cfg.schema;
 } else {
 title = SITE_NAME[locale];
 description = SEO_ROUTES["/"].description[idx];
 }
 }

 // 2. Applique les balises
 document.title = title;
 document.documentElement.setAttribute("lang", LOCALE_MAP[locale].html);
 upsertMeta("name", "description", description);
 upsertMeta("property", "og:title", title);
 upsertMeta("property", "og:description", description);
 upsertMeta("property", "og:url", SITE_URL + pathname);
 upsertMeta("property", "og:image", ogImage);
 upsertMeta("property", "og:image:width", "1200");
 upsertMeta("property", "og:image:height", "630");
 upsertMeta("property", "og:site_name", SITE_NAME[locale]);
 upsertMeta("property", "og:type", ogType);
 upsertMeta("property", "og:locale", LOCALE_MAP[locale].og);
 // Locale alternates
 Object.values(LOCALE_MAP).forEach((lm) => {
 if (lm.og !== LOCALE_MAP[locale].og) {
 // On garde un seul og:locale actif, les alternates sont via hreflang below
 }
 });
 upsertMeta("name", "twitter:card", "summary_large_image");
 upsertMeta("name", "twitter:title", title);
 upsertMeta("name", "twitter:description", description);
 upsertMeta("name", "twitter:image", ogImage);
 upsertMeta("name", "twitter:site", "@nandasa_africa");

 // 3. Canonical + hreflang (même URL pour toutes les langues. SPA côté client)
 upsertLink("canonical", SITE_URL + pathname);
 upsertLink("alternate", SITE_URL + pathname, { hreflang: "fr" });
 upsertLink("alternate", SITE_URL + pathname, { hreflang: "en" });
 upsertLink("alternate", SITE_URL + pathname, { hreflang: "pt" });
 upsertLink("alternate", SITE_URL + pathname, { hreflang: "x-default" });

 // 4. Schema.org, nettoie les précédents et injecte les données de la page
 clearDynamicJsonLd;

 // Breadcrumbs (sauf sur l'accueil)
 if (pathname !== "/") {
 injectJsonLd(buildBreadcrumb(pathname, SEG_LABELS[locale] || SEG_LABELS.fr, locale));
 }

 // Schema spécifique
 if (extraSchema) injectJsonLd(extraSchema);

 // Schema.org pages locales (implantations) : WebPage + Organization locale
 if (schemaKind === "local") {
 const cfg = SEO_ROUTES[pathname];
 const c = cfg && cfg.city;
 injectJsonLd({
 "@context": "https://schema.org",
 "@graph": [
 {
 "@type": "WebPage",
 name: title,
 description: description,
 url: SITE_URL + pathname,
 inLanguage: LOCALE_MAP[locale].htmlAlt,
 isPartOf: { "@type": "WebSite", url: SITE_URL, name: "NANDA SÁ" },
 },
 c && {
 "@type": "Organization",
 name: "NANDA SÁ",
 url: SITE_URL,
 email: "hello@nanda-sa.com",
 telephone: c.phone,
 address: { "@type": "PostalAddress", addressLocality: c.name, addressRegion: c.region, addressCountry: c.countryCode },
 areaServed: [
 { "@type": "City", name: c.name },
 { "@type": "AdministrativeArea", name: c.region },
 { "@type": "Country", name: c.country },
 ],
 },
 ].filter(Boolean),
 });
 }

 if (schemaKind === "home") {
 injectJsonLd({
 "@context": "https://schema.org",
 "@graph": [
 {
 "@type": "WebPage",
 name: title,
 description: description,
 url: SITE_URL + pathname,
 inLanguage: LOCALE_MAP[locale].htmlAlt,
 isPartOf: { "@type": "WebSite", url: SITE_URL, name: "NANDA SÁ" },
 },
 {
 "@type": "Organization",
 "@id": `${SITE_URL}/#organization`,
 name: "NANDA SÁ",
 url: SITE_URL,
 logo: `${SITE_URL}/logo.svg`,
 email: "hello@nanda-sa.com",
 address: [
 { "@type": "PostalAddress", addressLocality: "Ziguinchor", addressCountry: "SN" },
 { "@type": "PostalAddress", addressLocality: "Canchungo", addressRegion: "Cacheu", addressCountry: "GW" },
 ],
 areaServed: ["Senegal", "Guinea-Bissau", "West Africa"],
 contactPoint: [
 { "@type": "ContactPoint", contactType: "sales", telephone: "+221710090410", areaServed: "SN", availableLanguage: ["fr", "en"] },
 { "@type": "ContactPoint", contactType: "sales", telephone: "+245956828351", areaServed: "GW", availableLanguage: ["fr", "pt", "en"] },
 ],
 sameAs: [
 "https://www.instagram.com/nandasa_africa",
 "https://www.linkedin.com/company/nanda-s%C3%A1/",
 "https://www.facebook.com/nandasa.africa",
 ],
 },
 ],
 });
 }

 if (schemaKind === "brandsIndex") {
 injectJsonLd({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description: description,
 url: SITE_URL + pathname,
 mainEntity: {
 "@type": "ItemList",
 itemListElement: BRANDS.map((b, i) => ({
 "@type": "ListItem",
 position: i + 1,
 name: b.name,
 url: `${SITE_URL}/marques/${b.slug}`,
 })),
 },
 });
 }

 if (schemaKind === "tresors") {
 injectJsonLd({
 "@context": "https://schema.org",
 "@type": "CollectionPage",
 name: title,
 description: description,
 url: SITE_URL + pathname,
 mainEntity: {
 "@type": "ItemList",
 itemListElement: TRESORS.map((t, i) => ({
 "@type": "ListItem",
 position: i + 1,
 name: t.name,
 url: `${SITE_URL}/tresors/${t.slug}`,
 })),
 },
 });
 }

 if (schemaKind === "aboutPage") {
 injectJsonLd({
 "@context": "https://schema.org",
 "@type": "AboutPage",
 name: title,
 description: description,
 url: SITE_URL + pathname,
 about: { "@type": "Organization", name: "NANDA SÁ", url: SITE_URL },
 });
 }

 if (schemaKind === "contact") {
 injectJsonLd({
 "@context": "https://schema.org",
 "@type": "ContactPage",
 name: title,
 description: description,
 url: SITE_URL + pathname,
 });
 }

 if (schemaKind === "blog" || schemaKind === "news") {
 injectJsonLd({
 "@context": "https://schema.org",
 "@type": schemaKind === "blog" ? "Blog" : "CollectionPage",
 name: title,
 description: description,
 url: SITE_URL + pathname,
 inLanguage: LOCALE_MAP[locale].htmlAlt,
 });
 }
 }, [pathname, locale, idx, brandMatch, tresorMatch, articleMatch]);

 return null;
}
